import { useEffect, useState } from 'react'
import { postAssistant, type AssistantAction } from '../api'
import { assistantProgressCopy } from '../reviewProgress'
import type { Finding } from '../types'
import { getBodyText, getSelectionText } from '../word'
import { t } from '../i18n'

interface Props {
  token: string
  findings: Finding[]
}

const ACTIONS: { id: AssistantAction; label: string }[] = [
  { id: 'summarize_redlines', label: 'Підсумувати ключові правки' },
  { id: 'check_defined_terms', label: 'Перевірити визначені терміни' },
  { id: 'extract_key_terms', label: 'Витягнути ключові умови' },
  { id: 'check_placeholders', label: 'Знайти плейсхолдери' },
  { id: 'translate_redlines', label: 'Перекласти правки' },
]

export function AssistantPanel({ token, findings }: Props) {
  const [question, setQuestion] = useState('')
  const [scope, setScope] = useState<'file' | 'selection'>('file')
  const [busy, setBusy] = useState(false)
  const [answer, setAnswer] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState<AssistantAction | 'ask'>('ask')

  async function run(action: AssistantAction, asked?: string) {
    setBusy(true)
    setError(null)
    setAnswer(null)
    setActive(action)
    try {
      const documentText =
        scope === 'selection' ? await getSelectionText() : await getBodyText()
      const result = await postAssistant({
        token,
        action,
        question: asked,
        documentText,
        findings: findings.map((f) => ({
          finding_type: f.finding_type,
          change_summary: f.finding_summary || f.change_summary,
          justification: f.justification,
          clause_ref: f.clause_ref,
          importance: f.importance,
        })),
      })
      setAnswer(result.answer)
    } catch (err) {
      setAnswer(null)
      setError(err instanceof Error ? err.message : t('Асистент недоступний.'))
    } finally {
      setBusy(false)
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const asked = question.trim()
    if (!asked) return
    void run('ask', asked)
  }

  return (
    <div className="assistant" aria-busy={busy}>
      <div className="card assistant-hi">
        <p>{t('Привіт! Я асистент EUDOKIA. Чим допомогти?')}</p>
      </div>

      <form className="card" onSubmit={submit}>
        <label>{t('Що аналізувати')}<select
            value={scope}
            onChange={(e) => setScope(e.target.value as 'file' | 'selection')}
            disabled={busy}
          >
            <option value="file">{t('Поточний файл')}</option>
            <option value="selection">{t('Виділений фрагмент')}</option>
          </select>
        </label>
        <div className="assistant-ask">
          <label htmlFor="assistant-question">{t('Своє запитання')}</label>
          <div className="assistant-compose">
            <input
              id="assistant-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('Наприклад: який строк оплати в цьому договорі?')}
              disabled={busy}
            />
            <button
              type="submit"
              disabled={busy || !question.trim()}
              className="send"
              title={
                question.trim()
                  ? t('Надіслати запитання з поля вище')
                  : t('Спочатку введіть запитання')
              }
            >
              {busy && active === 'ask' ? t('Надсилаю…') : t('Надіслати')}
            </button>
          </div>
        </div>
        <div className="assistant-actions">
          <p className="muted small">{t('Або оберіть готову дію — вона запуститься одразу')}</p>
          {ACTIONS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={active === item.id ? 'action-chip active' : 'action-chip'}
              disabled={busy}
              onClick={() => void run(item.id)}
            >
              {t(item.label)}
            </button>
          ))}
        </div>
      </form>

      {busy ? <AssistantWait action={active} /> : null}
      {error ? <p className="error">{error}</p> : null}
      {answer && !busy ? (
        <section className="card justification assistant-answer">{answer}</section>
      ) : null}
    </div>
  )
}

function useElapsedSeconds(running: boolean): number {
  const [sec, setSec] = useState(0)
  useEffect(() => {
    if (!running) {
      setSec(0)
      return
    }
    setSec(0)
    const id = window.setInterval(() => setSec((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [running])
  return sec
}

function AssistantWait({ action }: { action: AssistantAction | 'ask' }) {
  const elapsedSec = useElapsedSeconds(true)
  const { title, hint } = assistantProgressCopy({ action, elapsedSec })
  return (
    <div className="progress-block" aria-live="polite">
      <p className="progress">{title}</p>
      <div
        className="progress-bar is-waiting"
        role="progressbar"
        aria-label={title}
      >
        <span />
      </div>
      <p className="progress-hint">{hint}</p>
    </div>
  )
}
